import { css } from '@emotion/react'
import { NextPage } from 'next'
import { colors, mq } from 'plume-ui'
import { CardTitle } from '../../atoms/CardTitle'
import { CompanyHead } from '../../common/Head'
import { Template } from '../../common/Template'
import { CompanyFinanceInfo } from './molecules/domestic/CompanyFinanceInfo'
import { useUser } from '../../../contexts/AuthContext'
import {
  isExited,
  isIPO,
  isPrivate,
  isResearchContinue,
  isResearchDone,
} from '@/lib/company'
import { isEnterpriseWithTrial, isGuest } from '@/lib/user'
import Error from '../../../pages/_error'
import {
  CompanyTabType,
  ContentfulNpStartupFloatingBanner,
  ContentfulNpStartupSlideArticle,
  ContentfulNpStartupStaticBanner,
} from '../../../types'
import {
  CompanyContactPerson,
  CompanyData,
  CompanyMemo,
  Status,
} from '../../../types/api'
import { MobileGuestFloatingBanner } from '../../common/MobileGuestFloatingBanner'
import { throttle } from 'lodash'
import { MobileGuestSlideBanner } from '../../common/MobileGuestSlideBanner'
import { ThumbnailFloatingBanner } from '../../common/ThumbnailFloatingBanner'
import { NewsPicksStaticBanner } from '../../common/npStartup/NewsPicksStaticBanner'
import { useEffect, useState } from 'react'
import { companyTabList } from './molecules/domestic/CompanyTabGroup'
import { CompanyBasicInfo } from './molecules/domestic/CompanyBasicInfo'
import { DatasourceLink } from './molecules/DatasourceLink'
import { CompanyFundingRounds } from './molecules/domestic/CompanyFundingRounds'
import { DirectorsTable } from './molecules/DirectorsTable'
import { EmployeesShiftTable } from './molecules/EmployeesShiftTable'
import { PartnershipsTable } from './molecules/PartnershipsTable'
import { OverseaBenchmarkMappingTable } from './molecules/oversea/OverseaBenchmarkMappingTable'
import { CompanyInviewForActiveTab } from './molecules/domestic/CompanyInviewForActiveTab'
import { CompanyIPOData } from './molecules/domestic/CompanyIPOData'
import { CompanyFinanceNewsList } from './molecules/domestic/CompanyFinanceNewsList'
import { CompanyNewsPicksFeeds } from './molecules/domestic/CompanyNewsPicksFeeds'
import { CompanyNewsFeed } from './molecules/domestic/CompanyNewsFeed'
import { CompanyOtherInfos } from './molecules/domestic/CompanyOtherInfos'
import { PerformancesTable } from './molecules/PerformancesTable'
import { CompanyShareholders } from './molecules/domestic/CompanyShareholders'
import { IGNORED_BY_WOVN_CLASS_NAME } from '@/lib/wovn'
import { FormattedDate } from '../../atoms/FormattedDate'
import { DomesticCompanyHeader } from './DomesticCompanyHeader'
import {
  ActiveTabMap,
  createInViewEventQueue,
  setupActiveTabs,
} from '@/components/common/InviewForActiveTab'
import { DomesticBenchmarkTable } from './molecules/domestic/DomesticBenchmarkTable'

const STICKY_HEADER_OFFSET = '-190px'

const [push, apply] = createInViewEventQueue()

type Props = {
  company: Required<CompanyData> | null
  slideBannerArticles: ContentfulNpStartupSlideArticle[] | null
  staticBanners: ContentfulNpStartupStaticBanner[] | null
  floatingBanner: ContentfulNpStartupFloatingBanner[] | null
  smartroundUrl: string | null
  companyMemo: CompanyMemo | null
  companyStatus: Status | null
  companyContactPerson: CompanyContactPerson | null
  reCaptchaEnabled: boolean
}

const CompanyPage: NextPage<Props> = (props) => {
  const user = useUser()
  // useReCaptchaOnMount('page_view', props.reCaptchaEnabled)

  const [init, first] = setupActiveTabs(companyTabList)
  const [activeTabs, setActiveTabs] =
    useState<ActiveTabMap<CompanyTabType>>(init())
  const [isPopUpVisible, setIsPopUpVisible] = useState(true)
  const [isFloatingBannerVisible, setIsFloatingBannerVisible] = useState(true)

  const handleChangeActive = (tab: CompanyTabType, inView: boolean) => {
    push(tab, inView, onTimeout)
  }

  const onTimeout = () => {
    setActiveTabs(apply(activeTabs))
  }

  const checkFooterInView = () => {
    const footer = document.getElementById('footer')
    if (footer === null) return
    const footerHeight = footer.offsetTop - window.innerHeight
    if (footerHeight < window.scrollY) {
      setIsPopUpVisible(false)
    } else {
      setIsPopUpVisible(true)
    }
  }

  const rootMargin = `${STICKY_HEADER_OFFSET} 0px`
  const throttledCheckFooterInView = throttle(checkFooterInView, 200)

  useEffect(() => {
    window.addEventListener('scroll', throttledCheckFooterInView)
    return () => {
      window.removeEventListener('scroll', throttledCheckFooterInView)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!props.company) {
    return <Error err={{ name: 'Company', message: "doesn't exist" }} />
  }

  if (
    isGuest(user) &&
    isResearchDone(props.company) &&
    !isExited(props.company)
  ) {
    return (
      <>
        <CompanyHead company={props.company}>
          {isEnterpriseWithTrial(user) ? (
            <meta name="viewport" content="width=1200px" key="viewport" />
          ) : null}
        </CompanyHead>
        <Template>
          <DomesticCompanyHeader
            company={props.company}
            status={props.companyStatus}
            memo={props.companyMemo}
            contactPerson={props.companyContactPerson}
            activeTab={first(activeTabs)}
            user={user}
          />
        </Template>
      </>
    )
  }

  return (
    <>
      <CompanyHead company={props.company}>
        {isEnterpriseWithTrial(user) ? (
          <meta name="viewport" content="width=1200px" key="viewport" />
        ) : null}
      </CompanyHead>
      <Template>
        <DomesticCompanyHeader
          company={props.company}
          status={props.companyStatus}
          memo={props.companyMemo}
          contactPerson={props.companyContactPerson}
          activeTab={first(activeTabs)}
          user={user}
        />
        <div
          css={css`
            background-color: ${colors.gray40};
            padding-bottom: 55px;
          `}
        >
          <div
            css={css`
              max-width: 1200px;
              margin: 0 auto;
            `}
          >
            <CompanyInviewForActiveTab
              tabName="basic-info"
              onChangeActive={handleChangeActive}
              offset={'-12em'}
              mobileOffset={'-5em'}
              stickyOffset={rootMargin}
              css={css`
                padding-top: 24px;
                ${mq.untilMedium} {
                  padding-top: 16px;
                }
              `}
            >
              <CompanyBasicInfo
                company={props.company}
                smartroundUrl={props.smartroundUrl}
              />
            </CompanyInviewForActiveTab>
            {isGuest(user) && (
              <div
                css={css`
                  margin: 64px 0 34px;
                  ${mq.untilMedium} {
                    display: none;
                  }
                `}
              >
                <NewsPicksStaticBanner
                  staticBanner={
                    props.staticBanners ? props.staticBanners[0] : null
                  }
                  trackingId="companypage-static-banner"
                />
              </div>
            )}
            {isGuest(user) && (
              <div
                css={css`
                  margin-top: 16px;
                `}
              >
                <MobileGuestSlideBanner
                  slideBannerArticles={props.slideBannerArticles}
                  trackingIdPrefix="companypage"
                />
              </div>
            )}
            {isEnterpriseWithTrial(user) && (
              <>
                <CompanyInviewForActiveTab
                  tabName="finance-info"
                  onChangeActive={handleChangeActive}
                  offset={'-12em'}
                  mobileOffset={'-5em'}
                  stickyOffset={rootMargin}
                  css={css`
                    padding-top: 40px;
                    ${mq.untilMedium} {
                      padding-top: 32px;
                    }
                  `}
                >
                  <CardTitle
                    right={
                      <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
                    }
                  >
                    ファイナンス
                  </CardTitle>
                  {isPrivate(props.company) && (
                    <CompanyFinanceInfo
                      info={props.company.financeBasicInfo}
                      withoutDilutiveShares={
                        props.company.withoutDilutiveShares
                      }
                    />
                  )}
                  <CompanyFundingRounds list={props.company.fundingRounds} />
                </CompanyInviewForActiveTab>
                {isIPO(props.company) && (
                  <CompanyInviewForActiveTab
                    tabName="ipo-data"
                    onChangeActive={handleChangeActive}
                    offset={'-12em'}
                    mobileOffset={'-5em'}
                    stickyOffset={rootMargin}
                    css={css`
                      padding-top: 40px;
                      ${mq.untilMedium} {
                        padding-top: 32px;
                      }
                    `}
                  >
                    <CardTitle
                      right={
                        <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
                      }
                    >
                      IPOデータ
                    </CardTitle>
                    <CompanyIPOData
                      ipoData={props.company.ipoData}
                      preIPOData={props.company.preIpoData}
                      ipoShareholders={props.company.ipoShareholders}
                      customLink={props.company.customLink}
                    />
                  </CompanyInviewForActiveTab>
                )}
              </>
            )}
            <CompanyInviewForActiveTab
              tabName="news"
              onChangeActive={handleChangeActive}
              offset={'-12em'}
              mobileOffset={'-5em'}
              stickyOffset={rootMargin}
              css={css`
                padding-top: 3rem;
                ${mq.untilMedium} {
                  padding-top: 32px;
                }
              `}
            >
              <CardTitle>ニュース</CardTitle>
              <CompanyFinanceNewsList financeNews={props.company.financeNews} />
              <CompanyNewsPicksFeeds
                shortname={props.company.shortname}
                css={css`
                  margin-block-start: 16px;
                  ${mq.large} {
                    margin-block-start: 24px;
                  }
                `}
              />
              <CompanyNewsFeed
                shortname={props.company.shortname}
                css={css`
                  margin-block-start: 16px;
                  ${mq.large} {
                    margin-block-start: 24px;
                  }
                `}
                feed={props.company.newsFeed}
              />
            </CompanyInviewForActiveTab>
            {isGuest(user) && (
              <div
                css={css`
                  margin-top: 64px;
                  ${mq.untilMedium} {
                    display: none;
                  }
                `}
              >
                <NewsPicksStaticBanner
                  staticBanner={
                    props.staticBanners ? props.staticBanners[1] : null
                  }
                  trackingId="companypage-static-banner-2"
                />
              </div>
            )}
            {isPrivate(props.company) && (
              <CompanyInviewForActiveTab
                tabName="shareholders"
                onChangeActive={handleChangeActive}
                offset={'-12em'}
                mobileOffset={'-5em'}
                stickyOffset={rootMargin}
                css={css`
                  padding-top: 40px;
                  ${mq.untilMedium} {
                    padding-top: 32px;
                  }
                `}
              >
                <CardTitle
                  sub={
                    isResearchContinue(props.company) ? (
                      <>
                        <span className={IGNORED_BY_WOVN_CLASS_NAME}>
                          <FormattedDate>
                            {props.company.shareholdersUpdatedAt}
                          </FormattedDate>
                        </span>
                        更新
                      </>
                    ) : (
                      ''
                    )
                  }
                  right={
                    <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
                  }
                >
                  株主
                </CardTitle>
                <CompanyShareholders
                  institutionalShareholders={
                    props.company.institutionalShareholders
                  }
                  angelShareholders={props.company.angelShareholders}
                  isEnterprise={isEnterpriseWithTrial(user)}
                />
              </CompanyInviewForActiveTab>
            )}
            <CompanyInviewForActiveTab
              tabName="directors"
              onChangeActive={handleChangeActive}
              offset={'-12em'}
              mobileOffset={'-5em'}
              stickyOffset={rootMargin}
              css={css`
                padding-top: 40px;
                ${mq.untilMedium} {
                  padding-top: 32px;
                }
              `}
            >
              <CardTitle
                sub={
                  isResearchContinue(props.company) &&
                  props.company.directorsUpdatedAt ? (
                    <>
                      <span className={IGNORED_BY_WOVN_CLASS_NAME}>
                        <FormattedDate>
                          {props.company.directorsUpdatedAt}
                        </FormattedDate>
                      </span>
                      更新
                    </>
                  ) : undefined
                }
                right={
                  <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
                }
              >
                役員
              </CardTitle>
              <DirectorsTable
                title={
                  isPrivate(props.company) ? '役員（現職）' : '役員（IPO時）'
                }
                directors={props.company.directors}
                isEnterprise={isEnterpriseWithTrial(user)}
              />
            </CompanyInviewForActiveTab>
            <CompanyInviewForActiveTab
              tabName="performances"
              onChangeActive={handleChangeActive}
              offset={'-12em'}
              mobileOffset={'-5em'}
              stickyOffset={rootMargin}
              css={css`
                padding-top: 40px;
                ${mq.untilMedium} {
                  padding-top: 32px;
                }
              `}
            >
              <CardTitle
                sub={
                  isPrivate(props.company) &&
                  props.company.performancesUpdatedAt ? (
                    <>
                      <span className={IGNORED_BY_WOVN_CLASS_NAME}>
                        <FormattedDate>
                          {props.company.performancesUpdatedAt}
                        </FormattedDate>
                      </span>
                      更新
                    </>
                  ) : undefined
                }
              >
                業績
              </CardTitle>
              <PerformancesTable
                performances={props.company.performances}
                legacyPerformances={props.company.legacyPerformances}
                isEnterprise={isEnterpriseWithTrial(user)}
              />
            </CompanyInviewForActiveTab>
            {props.company.employeesShift?.length > 0 && (
              <CompanyInviewForActiveTab
                tabName="employees"
                onChangeActive={handleChangeActive}
                offset={'-12em'}
                mobileOffset={'-5em'}
                stickyOffset={rootMargin}
                css={css`
                  padding-top: 40px;
                  ${mq.untilMedium} {
                    padding-top: 32px;
                  }
                `}
              >
                <CardTitle
                  right={
                    <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
                  }
                >
                  従業員数
                </CardTitle>
                <EmployeesShiftTable
                  employeesShift={props.company.employeesShift}
                  isEnterprise={isEnterpriseWithTrial(user)}
                />
              </CompanyInviewForActiveTab>
            )}
            <CompanyInviewForActiveTab
              tabName="partnerships"
              onChangeActive={handleChangeActive}
              offset={'-12em'}
              mobileOffset={'-5em'}
              stickyOffset={rootMargin}
              css={css`
                padding-top: 40px;
                ${mq.untilMedium} {
                  padding-top: 32px;
                }
              `}
            >
              <CardTitle
                right={
                  <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
                }
              >
                提携先
              </CardTitle>
              <PartnershipsTable
                partnerships={props.company.partnershipRounds}
                isEnterprise={isEnterpriseWithTrial(user)}
              />
            </CompanyInviewForActiveTab>
            {isEnterpriseWithTrial(user) && (
              <>
                <CompanyInviewForActiveTab
                  tabName="benchmarks"
                  onChangeActive={handleChangeActive}
                  offset={'-12em'}
                  mobileOffset={'-5em'}
                  stickyOffset={rootMargin}
                  css={css`
                    padding-top: 40px;
                    ${mq.untilMedium} {
                      padding-top: 32px;
                    }
                  `}
                >
                  <CardTitle
                    className="benchmark"
                    helpText="類似企業の選定について"
                    helpProps={{
                      linkTo: process.env.BENCHMARK_HELP_ENDPOINT,
                      className: 'help-icon',
                      size: 'large',
                    }}
                    dataTestId="benchmark-section-title"
                  >
                    類似企業
                  </CardTitle>
                  <DomesticBenchmarkTable
                    shortname={props.company.shortname}
                    css={css`
                      margin-bottom: 2rem;
                    `}
                  />
                  <OverseaBenchmarkMappingTable
                    shortname={props.company.shortname}
                  />
                </CompanyInviewForActiveTab>
                <CompanyInviewForActiveTab
                  tabName="others"
                  onChangeActive={handleChangeActive}
                  offset={'-12em'}
                  mobileOffset={'-5em'}
                  stickyOffset={rootMargin}
                  css={css`
                    padding-top: 40px;
                    ${mq.untilMedium} {
                      padding-top: 32px;
                    }
                  `}
                >
                  <CardTitle>その他</CardTitle>
                  <CompanyOtherInfos company={props.company} />
                </CompanyInviewForActiveTab>
              </>
            )}
          </div>
        </div>
      </Template>
      {isGuest(user) && isPopUpVisible && (
        <MobileGuestFloatingBanner trackingIdPrefix="companypage" />
      )}
      {isGuest(user) && isFloatingBannerVisible && (
        <ThumbnailFloatingBanner
          onClose={() => setIsFloatingBannerVisible(false)}
          floatingBanner={props.floatingBanner}
          trackingIdPrefix=""
        />
      )}
    </>
  )
}

export default CompanyPage
